/** Start an invoice from an offer, a request or a customer. */
import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  type CustomerResponse,
  type InvoiceResponse,
  type OfferResponse,
  type RequestResponse,
} from "@offertli/shared";
import { Button } from "../../components/ui/Button.js";
import { apiGet, apiPost } from "../../lib/api.js";
import { Field } from "../../components/ui/Field.js";
import { PageHeader } from "../../components/ui/PageHeader.js";
import { MutationError } from "../shared.js";
import { CustomerPicker, Notice, useCustomer } from "../commercial/document.js";

export function InvoiceNewPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const offerId = params.get("offerId");
  const requestId = params.get("requestId");
  const offer = useQuery({
    queryKey: ["offer", offerId],
    queryFn: () =>
      apiGet<{ data: OfferResponse }>(`/offers/${offerId}`).then(
        (response) => response.data,
      ),
    enabled: Boolean(offerId),
  });
  const request = useQuery({
    queryKey: ["request", requestId],
    queryFn: () =>
      apiGet<{ data: RequestResponse }>(`/requests/${requestId}`).then(
        (response) => response.data,
      ),
    enabled: Boolean(requestId),
  });
  const preselected = useCustomer(params.get("customerId"));
  const [customer, setCustomer] = useState<CustomerResponse | null>(null);
  useEffect(() => {
    if (preselected.data && !customer) setCustomer(preselected.data);
  }, [preselected.data, customer]);
  const create = useMutation({
    mutationFn: () =>
      apiPost<{ data: InvoiceResponse }>(
        "/invoices",
        offerId
          ? { offerId }
          : requestId
            ? { requestId }
            : { customerId: customer!.id },
      ),
    onSuccess: (response) => navigate(`/invoices/${response.data.id}`),
  });
  const source = offer.data
    ? t("invoice.fromOffer", { number: offer.data.displayNumber })
    : request.data
      ? t("invoice.fromRequest", { number: request.data.number })
      : null;
  return (
    <div className="mx-auto max-w-xl">
      <PageHeader
        title={t("invoice.new")}
        subtitle={t("invoice.newSubtitle")}
      />
      <div className="space-y-4 rounded-xl border border-app-border bg-app-surface p-5">
        {source ? (
          <Notice>{source}</Notice>
        ) : (
          <Field label={t("common.customer")} required htmlFor="inv-customer">
            <CustomerPicker
              id="inv-customer"
              value={customer}
              onChange={setCustomer}
            />
          </Field>
        )}
        <p className="text-sm text-app-muted">{t("invoice.draftHint")}</p>
        <MutationError error={create.error} />
        <div className="flex justify-end gap-2">
          <Button
            variant="text"
            severity="secondary"
            onClick={() => navigate(-1)}
          >
            {t("common.cancel")}
          </Button>
          <Button
            loading={create.isPending || undefined}
            disabled={!offerId && !requestId && !customer}
            onClick={() => create.mutate()}
          >
            {t("invoice.createDraft")}
          </Button>
        </div>
      </div>
    </div>
  );
}
